import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('admin_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('admin_token');
      if (window.location.pathname.startsWith('/bambam')) {
        window.location.href = '/bambam/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  verify: () => api.get('/auth/verify'),
};

// Settings
export const settingsAPI = {
  get: () => api.get('/settings'),
  update: (data) => api.put('/settings', data),
};

// Products
export const productsAPI = {
  getAll: () => api.get('/products'),
  create: (data) => api.post('/products', data),
  update: (id, data) => api.put(`/products/${id}`, data),
  delete: (id) => api.delete(`/products/${id}`),
};

// Reviews
export const reviewsAPI = {
  getAll: () => api.get('/reviews'),
  create: (data) => api.post('/reviews', data),
  update: (id, data) => api.put(`/reviews/${id}`, data),
  delete: (id) => api.delete(`/reviews/${id}`),
};

// Pages
export const pagesAPI = {
  getAll: () => api.get('/pages'),
  getBySlug: (slug) => api.get(`/pages/${slug}`),
  create: (data) => api.post('/pages', data),
  update: (id, data) => api.put(`/pages/${id}`, data),
  delete: (id) => api.delete(`/pages/${id}`),
};

// Navigation
export const navigationAPI = {
  getAll: () => api.get('/navigation'),
  getByLocation: (location) => api.get(`/navigation/${location}`),
  create: (data) => api.post('/navigation', data),
  update: (id, data) => api.put(`/navigation/${id}`, data),
  delete: (id) => api.delete(`/navigation/${id}`),
};

// Content Sections
export const contentAPI = {
  getAll: () => api.get('/content'),
  get: (key) => api.get(`/content/${key}`),
  update: (key, data) => api.put(`/content/${key}`, data),
};

// Media
export const mediaAPI = {
  getAll: () => api.get('/media'),
  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/media/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  delete: (id) => api.delete(`/media/${id}`),
};

// Special Uploads (Favicon, OG Image, Verification Files)
export const uploadAPI = {
  favicon: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/favicon', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  ogImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/og-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  verification: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/upload/verification', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getVerificationFiles: () => api.get('/verification-files'),
  deleteVerificationFile: (filename) => api.delete(`/verification-files/${filename}`),
};

// AI Generation
export const aiAPI = {
  generate: (data) => api.post('/ai/generate', data),
};

// Click Tracking
export const clicksAPI = {
  track: (data) => api.post('/clicks/track', data),
  getStats: () => api.get('/clicks/stats'),
};

// Initialize data
export const initAPI = {
  init: () => api.post('/init'),
};

export default api;
